import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApolloError, buildUseQuery } from '@main/core-ui';
import { endpoints, parsePrivacyCenter, PrivacyCenter } from '@main/pc-types';

import { selectPrivacyCenter } from './selectors';
import { setPrivacyCenter } from './slice';

const usePrivacyCenter = buildUseQuery(endpoints.privacyCenter);
const usePrivacyCenterVersionModules = buildUseQuery(
  endpoints.privacyCenterVersionModules,
);

/**
 * Fetch the current config of privacy center
 *
 * @returns Data, loading, error
 */
export function useParsedPrivacyCenter(): {
  /** Privacy center */
  privacyCenter?: PrivacyCenter;
  /** Loading */
  loading: boolean;
  /** Error */
  error?: ApolloError;
} {
  const dispatch = useDispatch();
  const privacyCenter = useSelector(selectPrivacyCenter);

  // Fetch the latest saved privacy center config
  // TODO: https://transcend.height.app/T-44689 - instead of using the privacy center query, we should use the privacy center version modules query
  const { data, loading, error } = usePrivacyCenter({
    fetchPolicy: 'cache-and-network',
  });

  // Fetch the currently deployed privacy center config
  const { data: versionModules } = usePrivacyCenterVersionModules({
    variables: {
      input: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        privacyCenterId: privacyCenter?.id!,
        inDeployState: true,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !privacyCenter?.id,
  });

  useEffect(() => {
    if (data) {
      const parsedPrivacyCenter = parsePrivacyCenter(data);
      let parsedDeployedPrivacyCenter;

      const rawModule = versionModules?.privacyCenterModule;
      if (rawModule && rawModule !== '{}') {
        parsedDeployedPrivacyCenter = parsePrivacyCenter(JSON.parse(rawModule));
      }

      dispatch(
        setPrivacyCenter({
          privacyCenter: parsedPrivacyCenter,
          ...(parsedDeployedPrivacyCenter && {
            deployedPrivacyCenter: parsedDeployedPrivacyCenter,
          }),
        }),
      );
    }
  }, [data, versionModules]);

  return {
    privacyCenter,
    loading,
    error,
  };
}
